import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// =======================
// GLOBAL STYLES (без изменений)
// =======================
const Page = styled.div`
  background: #0a0a10;
  color: #e0e0ff;
  overflow: hidden;
  position: relative;
  min-height: 100vh;
`;

const Section = styled.section`
  padding: 8rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 5rem 1rem;
  }
`;

// =======================
// HERO
// =======================
const Hero = styled(Section)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: 
    radial-gradient(circle at 30% 20%, rgba(18, 85, 253, 0.12), transparent 50%),
    radial-gradient(circle at 70% 80%, rgba(0, 238, 255, 0.1), transparent 50%),
    #0a0a10;
`;

const HeroTitle = styled.h1`
  font-size: 5.5rem;
  font-weight: 900;
  background: linear-gradient(120deg, #00f5ff, #1255fd, #00eeffff);
  background-size: 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: flow 16s ease infinite;
  margin: 0 0 1.5rem;

  @keyframes flow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @media (max-width: 768px) {
    font-size: 3.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.4rem;
  font-weight: 300;
  color: #aaaaff;
  max-width: 700px;
  line-height: 1.7;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

// =======================
// STORY BLOCK
// =======================
const StoryBlock = styled(Section)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  &:nth-of-type(2n+2) {
    direction: rtl;
    & > * {
      direction: ltr;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    &:nth-of-type(2n) {
      direction: ltr;
    }
  }
`;

const StoryImage = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, #1e90ff, #00bfff);
  border-radius: 28px;
  box-shadow: 0 20px 50px rgba(0, 191, 255, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="none"/><path d="M20,50 Q40,30 60,50 T100,50" stroke="rgba(255,255,255,0.1)" stroke-width="2" fill="none"/></svg>') repeat;
    animation: wave 12s linear infinite;
    background-size: 100px 100px;
  }

  @keyframes wave {
    0% { transform: translateX(0); }
    100% { transform: translateX(-100px); }
  }

  @media (max-width: 768px) {
    height: 280px;
  }
`;

const StoryContent = styled.div``;

const StoryTitle = styled.h2`
  font-size: 3.2rem;
  font-weight: 900;
  color: white;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const StoryText = styled.p`
  font-size: 1.15rem;
  color: #ccc;
  line-height: 1.8;
  font-weight: 300;
`;

// =======================
// VALUES
// =======================
const Values = styled(Section)`
  text-align: center;
`;

const ValuesTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 900;
  background: linear-gradient(120deg, #00eeffff, #7abdffff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    font-size: 2.6rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ValueCard = styled(motion.div)`
  background: rgba(30, 35, 60, 0.4);
  border: 1px solid rgba(100, 150, 255, 0.2);
  border-radius: 24px;
  padding: 2rem;
  backdrop-filter: blur(12px);
  text-align: center;
  transition: transform 0.2s ease;

  h3 {
    font-size: 1.6rem;
    font-weight: 800;
    color: white;
    margin: 1rem 0 0.75rem;
  }

  p {
    color: #aaa;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

// =======================
// TEAM TEASER
// =======================
const TeamTeaser = styled(Section)`
  text-align: center;
  background: rgba(18, 85, 253, 0.05);
  border-radius: 32px;
  margin: 4rem auto;
  padding: 6rem 2rem;
  max-width: 1000px;

  @media (max-width: 768px) {
    margin: 2rem 1rem;
    padding: 4rem 1rem;
  }
`;

const TeamTitle = styled.h2`
  font-size: 3.2rem;
  font-weight: 900;
  color: white;
  margin-bottom: 1rem;
`;

const TeamText = styled.p`
  font-size: 1.2rem;
  color: #aaaaff;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.7;
`;

// =======================
// АНИМАЦИИ И КОНТЕЙНЕРЫ
// =======================
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerContainer = {
  whileInView: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

// =======================
// ABOUT PAGE — ПЕРЕВЕДЁННАЯ ВЕРСИЯ
// =======================
const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <Page>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        {/* HERO */}
        <Hero>
          <motion.div {...fadeUp}>
            <HeroTitle>{t("about_hero_title")}</HeroTitle>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <HeroSubtitle dangerouslySetInnerHTML={{ __html: t("about_hero_subtitle") }} />
          </motion.div>
        </Hero>

        {/* STORY 1 */}
        <StoryBlock>
          <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
            <StoryImage aria-label="Dream visualization" />
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
            <StoryContent>
              <StoryTitle>{t("about_dream_title")}</StoryTitle>
              <StoryText dangerouslySetInnerHTML={{ __html: t("about_dream_text") }} />
            </StoryContent>
          </motion.div>
        </StoryBlock>

        {/* STORY 2 */}
        <StoryBlock>
          <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
            <StoryImage aria-label="Vision visualization" />
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
            <StoryContent>
              <StoryTitle>{t("about_vision_title")}</StoryTitle>
              <StoryText dangerouslySetInnerHTML={{ __html: t("about_vision_text") }} />
            </StoryContent>
          </motion.div>
        </StoryBlock>

        {/* VALUES */}
        <Values>
          <motion.div {...fadeUp}>
            <ValuesTitle>{t("about_values_title")}</ValuesTitle>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{ once: true }}>
            <ValuesGrid>
              <ValueCard variants={fadeUp} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>Fast</div>
                <h3>{t("about_value_fast_title")}</h3>
                <p>{t("about_value_fast_desc")}</p>
              </ValueCard>

              <ValueCard variants={fadeUp} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>AI</div>
                <h3>{t("about_value_chat_title")}</h3>
                <p>{t("about_value_chat_desc")}</p>
              </ValueCard>

              <ValueCard variants={fadeUp} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>Future</div>
                <h3>{t("about_value_future_title")}</h3>
                <p>{t("about_value_future_desc")}</p>
              </ValueCard>
            </ValuesGrid>
          </motion.div>
        </Values>

        {/* TEAM TEASER */}
        <TeamTeaser>
          <motion.div {...fadeUp}>
            <TeamTitle>{t("about_team_title")}</TeamTitle>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <TeamText>{t("about_team_text")}</TeamText>
          </motion.div>
        </TeamTeaser>
      </motion.div>
    </Page>
  );
};

export default AboutPage;